/* eslint-disable camelcase */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { RefreshCw, X } from 'lucide-react'

import Board from './Board'
import Drawer from './Drawer'
import {
  activeFilters,
  defaultState,
  deltaTone,
  dimensionLabels,
  type FilterKey,
  filterKeyLabels,
  filterKeys,
  formatCount,
  formatDelta,
  formatValue,
  groupFilters,
  type PageState,
  readState,
  severityLabels,
  toApiFilters,
  type Window,
  windows,
  wisprUserValues,
  writeState,
} from './model'
import Trend from './Trend'
import { isAccessDenied, QUERY_ROOT, REFRESH_MS, useReportGroups, useReportOverview } from './useReportedMeetings'

import styles from './ReportedMeetingsPage.module.scss'

import { isApiError } from '@/api'
import { type ReportDimension, reportDimensions, reportTypes, severities } from '@/api/meetingReportsObservability'
import Button from '@/components/Button'
import Input from '@/components/Input'
import Select from '@/components/Select'
import { compactAmPm } from '@/utils/compactAmPm'

const windowLabels: Record<Window, string> = {
  '1d': 'Last 24 hours',
  '7d': 'Last 7 days',
  '14d': 'Last 14 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
}

const isTransientError = (error: unknown) => isApiError(error) && (error.status >= 500 || error.status === 429)

const StaleNotice = ({ onRetry }: { onRetry: () => void }) => (
  <p className={styles.staleNotice} role="status">
    Showing saved results. Refresh failed.{' '}
    <button type="button" onClick={onRetry}>
      Retry
    </button>
  </p>
)

const ReportedMeetingsPage = () => {
  const [search, setSearch] = useSearchParams()
  const queryClient = useQueryClient()
  const state = useMemo(() => readState(search), [search])
  const [now, setNow] = useState(() => new Date())
  const [draftSearch, setDraftSearch] = useState(state.search ?? '')
  useEffect(() => setDraftSearch(state.search ?? ''), [state.search])
  const [addFilter, setAddFilter] = useState<FilterKey | ''>('')
  const [accessDenied, setAccessDenied] = useState(false)
  const filters = useMemo(() => toApiFilters(state, now), [state, now])
  const overview = useReportOverview(filters)
  const groups = useReportGroups(filters, state.group_by)
  useEffect(
    () =>
      queryClient.getQueryCache().subscribe((event) => {
        if (event.query.queryKey[0] !== QUERY_ROOT) return
        if (isAccessDenied(event.query.state.error)) setAccessDenied(true)
        else if (event.query.state.status === 'success') setAccessDenied(false)
      }),
    [queryClient]
  )
  const update = useCallback(
    (patch: Partial<PageState>, options?: { replace?: boolean }) =>
      setSearch(writeState({ ...readState(search), ...patch }), options),
    [search, setSearch]
  )
  const closeDrawer = useCallback(() => update({ selected: undefined }), [update])
  const selectedGroup = state.selected ? groups.data?.groups.find((group) => group.value === state.selected) : undefined
  const drawerOpen = selectedGroup !== undefined
  useEffect(() => {
    if (state.selected && groups.data && !groups.isPlaceholderData && !selectedGroup)
      update({ selected: undefined }, { replace: true })
  }, [state.selected, groups.data, groups.isPlaceholderData, selectedGroup, update])
  const opener = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (drawerOpen || !opener.current) return
    if (opener.current.isConnected) opener.current.focus()
    opener.current = null
  }, [drawerOpen])
  useEffect(() => {
    if (drawerOpen) return
    const restamp = () => {
      if (document.visibilityState === 'visible') setNow(new Date())
    }
    const timer = window.setInterval(restamp, REFRESH_MS)
    document.addEventListener('visibilitychange', restamp)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', restamp)
    }
  }, [drawerOpen])
  const trendRange = useRef({ start_at: filters.start_at, end_at: filters.end_at })
  useEffect(() => {
    if (!overview.isPlaceholderData) trendRange.current = { start_at: filters.start_at, end_at: filters.end_at }
  })
  const { start_at: trendStart, end_at: trendEnd } = overview.isPlaceholderData ? trendRange.current : filters
  const summary = overview.data?.summary
  const previous = overview.data?.previous
  const metrics =
    summary &&
    previous &&
    ([
      ['Reports', summary.report_count, previous.report_count],
      ['S0 · data loss', summary.severity.s0, previous.severity.s0],
      ['Meetings', summary.meeting_count, previous.meeting_count],
      ['Signatures', summary.signature_count, previous.signature_count],
    ] as const)
  const facetOptions = (key: FilterKey): string[] => {
    if (key === 'severity') return [...severities]
    if (key === 'report_type') return [...reportTypes]
    if (key === 'wispr_user') return [...wisprUserValues]
    return overview.data?.facets[key] ?? []
  }
  const overviewStale = overview.isError && overview.data !== undefined && isTransientError(overview.error)
  const groupsStale = groups.isError && groups.data !== undefined && isTransientError(groups.error)
  return (
    <main className={styles.page}>
      <Helmet>
        <title>Reported meetings</title>
      </Helmet>
      <div className={styles.content}>
        <header className={styles.header}>
          <div>
            <p className={styles.eyebrow}>Meeting observability</p>
            <h1>Reported meetings</h1>
            <p className={styles.subtitle}>Rank report signatures by harm, then drill into the cohorts behind them.</p>
          </div>
          <div className={styles.headerActions}>
            <Button
              title={<RefreshCw size={15} />}
              ariaLabel="Refresh dashboard"
              type="light"
              isLoading={overview.isFetching || groups.isFetching}
              onClick={() => {
                setAccessDenied(false)
                setNow(new Date())
                void queryClient.invalidateQueries({
                  queryKey: [QUERY_ROOT],
                  refetchType: 'active',
                })
              }}
            />
          </div>
        </header>

        <form
          className={styles.filterBar}
          role="search"
          onSubmit={(event) => {
            event.preventDefault()
            update({
              search: draftSearch.trim() || undefined,
              selected: undefined,
            })
          }}
        >
          <Input
            type="search"
            value={draftSearch}
            onInput={setDraftSearch}
            placeholder="Search signature, device, feedback, known issue…"
            ariaLabel="Search reports"
            maxLength={200}
            className={styles.search}
          />
          <label className={styles.window}>
            <span className={styles.srOnly}>Time window</span>
            <Select
              id="reported-meetings-window"
              className={styles.control}
              value={state.window}
              options={windows.map((value) => ({
                value,
                label: windowLabels[value],
              }))}
              onChange={(value) => update({ window: value as Window, selected: undefined })}
            />
          </label>
          <label className={styles.addFilter}>
            <span className={styles.srOnly}>Add filter</span>
            <Select
              id="reported-meetings-add-filter"
              className={styles.control}
              value={addFilter}
              placeholder="Add filter…"
              options={filterKeys
                .filter((key) => state[key] === undefined && facetOptions(key).length > 0)
                .map((key) => ({ value: key, label: filterKeyLabels[key] }))}
              onChange={(value) => setAddFilter(value as FilterKey)}
            />
          </label>
          {addFilter && (
            <span className={styles.addFilterValue}>
              <label className={styles.filterValue}>
                <span className={styles.srOnly}>{filterKeyLabels[addFilter]}</span>
                <Select
                  id="reported-meetings-filter-value"
                  className={styles.control}
                  value=""
                  placeholder={`${filterKeyLabels[addFilter]}…`}
                  isLoading={overview.isFetching && !overview.data}
                  options={facetOptions(addFilter).map((value) => ({
                    value,
                    label: formatValue(addFilter, value),
                  }))}
                  onChange={(value) => {
                    setAddFilter('')
                    update({ [addFilter]: value, selected: undefined })
                  }}
                />
              </label>
              <button type="button" aria-label="Cancel add filter" onClick={() => setAddFilter('')}>
                <X size={14} />
              </button>
            </span>
          )}
          <label className={styles.groupBy}>
            <span>Group by</span>
            <Select
              id="reported-meetings-group-by"
              className={styles.control}
              value={state.group_by}
              options={reportDimensions.map((dimension) => ({
                value: dimension,
                label: dimensionLabels[dimension],
              }))}
              onChange={(value) =>
                update({
                  group_by: value as ReportDimension,
                  selected: undefined,
                })
              }
            />
          </label>
        </form>
        {(activeFilters(state).length > 0 || state.search) && (
          <div className={styles.chips} aria-label="Active filters">
            {state.search && (
              <span className={styles.chip}>
                search: “{state.search}”
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => {
                    setDraftSearch('')
                    update({ search: undefined, selected: undefined })
                  }}
                >
                  <X size={12} />
                </button>
              </span>
            )}
            {activeFilters(state).map(([key, value]) => (
              <span key={key} className={styles.chip}>
                {filterKeyLabels[key]}: {formatValue(key, value)}
                <button
                  type="button"
                  aria-label={`Remove ${filterKeyLabels[key]} filter`}
                  onClick={() => update({ [key]: undefined, selected: undefined })}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
            <button
              type="button"
              className={styles.textButton}
              onClick={() => {
                setDraftSearch('')
                setSearch(
                  writeState({
                    ...defaultState(),
                    group_by: state.group_by,
                    window: state.window,
                  })
                )
              }}
            >
              Clear all
            </button>
          </div>
        )}

        {accessDenied || (overview.isError && !overviewStale) ? (
          <div className={styles.error} role="alert">
            <span>{accessDenied ? 'You need internal admin access to view reports.' : 'Could not load reports.'}</span>
            {!accessDenied && <Button title="Retry" type="light" onClick={() => void overview.refetch()} />}
          </div>
        ) : overview.data && summary && metrics ? (
          <>
            {overviewStale && <StaleNotice onRetry={() => void overview.refetch()} />}
            <section className={styles.metrics} aria-label="Report totals">
              {metrics.map(([label, current, prior]) => (
                <div key={label} className={styles.metric}>
                  <span>{label}</span>
                  <strong>{formatCount(current)}</strong>
                  <small className={styles[deltaTone(current, prior)]}>{formatDelta(current, prior)} vs prior</small>
                </div>
              ))}
            </section>
            <Trend trend={overview.data.trend} startAt={trendStart} endAt={trendEnd} />
            <p className={styles.legend}>
              {severities.map((severity) => (
                <span key={severity}>
                  <i className={styles[severity]} aria-hidden="true" /> {severityLabels[severity]}
                </span>
              ))}
              <span className={styles.legendNote}>Weighted by S0×8, S1×4, S2×2, S3×1 when ranking rows.</span>
            </p>
            {groups.isError && !groupsStale ? (
              <div className={styles.error} role="alert">
                <span>Could not load groups.</span>
                <Button title="Retry" type="light" onClick={() => void groups.refetch()} />
              </div>
            ) : groups.data ? (
              <>
                {groupsStale && <StaleNotice onRetry={() => void groups.refetch()} />}
                <Board
                  dimension={groups.data.dimension}
                  groups={groups.data.groups}
                  truncated={groups.data.truncated}
                  selected={state.selected}
                  onSelect={(value) => {
                    const active = document.activeElement
                    opener.current = active instanceof HTMLElement && active !== document.body ? active : null
                    update({
                      selected: state.selected === value ? undefined : value,
                    })
                  }}
                />
              </>
            ) : (
              <div className={styles.loading} role="status">
                Loading groups…
              </div>
            )}
          </>
        ) : (
          <div className={styles.loading} role="status">
            Loading reported meetings…
          </div>
        )}
        <footer className={styles.footer}>
          <span>Classified reports only; unclassified and ML-training reports are excluded.</span>
          {overview.data && (
            <span>
              Latest report:{' '}
              {overview.data.latest_created_at
                ? compactAmPm(new Date(overview.data.latest_created_at).toLocaleString())
                : 'None in window'}{' '}
              · Refreshes every minute
            </span>
          )}
        </footer>
      </div>
      {selectedGroup && groups.data && (
        <Drawer
          key={`${groups.data.dimension}:${selectedGroup.value}`}
          dimension={groups.data.dimension}
          group={selectedGroup}
          filters={groupFilters(state, selectedGroup.value, now)}
          onClose={closeDrawer}
        />
      )}
    </main>
  )
}

export default ReportedMeetingsPage
